import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    BarChart3, 
    Users, 
    MessageSquare, 
    Eye, 
    FileText, 
    Mail,
    TrendingUp,
    Activity,
    ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAnalyticsPage = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const response = await axios.get(`${API}/admin/analytics`);
            setAnalytics(response.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, subtitle, icon: Icon, trend }) => (
        <Card className="bg-[#4A5240] border-[#5A6350]">
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-[#A0A0A0] text-sm">{title}</p>
                        <p className="text-3xl font-bold text-white mt-1">{value}</p>
                        {subtitle && (
                            <p className="text-[#C9A227] text-sm mt-1">{subtitle}</p>
                        )}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#C9A227]/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-[#C9A227]" />
                    </div>
                </div>
                {trend !== undefined && (
                    <div className="mt-3 flex items-center gap-1">
                        <TrendingUp className={`w-4 h-4 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`} />
                        <span className={`text-sm ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {trend >= 0 ? '+' : ''}{trend} this week
                        </span>
                    </div>
                )}
            </CardContent>
        </Card>
    );

    const SimpleBarChart = ({ data, labelKey, valueKey, maxValue }) => {
        const max = maxValue || Math.max(...data.map(d => d[valueKey]), 1);
        return (
            <div className="space-y-2">
                {data.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                        <span className="text-[#A0A0A0] text-sm w-20 truncate">{item[labelKey]}</span>
                        <div className="flex-1 bg-[#3D4435] rounded-full h-4 overflow-hidden">
                            <div 
                                className="bg-[#C9A227] h-full rounded-full transition-all duration-500"
                                style={{ width: `${(item[valueKey] / max) * 100}%` }}
                            />
                        </div>
                        <span className="text-white text-sm w-10 text-right">{item[valueKey]}</span>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A227]"></div>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="text-center py-12">
                <p className="text-[#A0A0A0]">Unable to load analytics data</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-white font-['Playfair_Display']">
                    Site Analytics
                </h1>
                <p className="text-[#A0A0A0] mt-1">
                    Track engagement and growth across the platform
                </p>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                    title="Total Page Views"
                    value={analytics.page_views.total.toLocaleString()}
                    subtitle={`${analytics.page_views.today} today`}
                    icon={Eye}
                    trend={analytics.page_views.this_week}
                />
                <StatCard 
                    title="Total Members"
                    value={analytics.members.total}
                    subtitle={`${analytics.members.active} active`}
                    icon={Users}
                    trend={analytics.members.new_this_week}
                />
                <StatCard 
                    title="Newsletter Subscribers"
                    value={analytics.newsletter.total_subscribers}
                    subtitle={`${analytics.newsletter.new_this_week} this week`}
                    icon={Mail}
                    trend={analytics.newsletter.new_this_week}
                />
                <StatCard 
                    title="Total Messages"
                    value={analytics.engagement.total_messages}
                    subtitle={`${analytics.engagement.messages_this_week} this week`}
                    icon={MessageSquare}
                    trend={analytics.engagement.messages_this_week}
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Page Views Trend */}
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <Activity className="w-5 h-5 text-[#C9A227]" />
                            Page Views (Last 7 Days)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-48 flex items-end justify-between gap-2 pt-4">
                            {analytics.page_views.daily_trend.map((day, idx) => {
                                const maxViews = Math.max(...analytics.page_views.daily_trend.map(d => d.views), 1);
                                const height = (day.views / maxViews) * 100;
                                return (
                                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                                        <div className="w-full bg-[#3D4435] rounded-t relative" style={{ height: '140px' }}>
                                            <div 
                                                className="absolute bottom-0 w-full bg-[#C9A227] rounded-t transition-all duration-500"
                                                style={{ height: `${height}%` }}
                                            />
                                        </div>
                                        <span className="text-[#A0A0A0] text-xs">
                                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                                        </span>
                                        <span className="text-white text-xs font-medium">{day.views}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* New Members Trend */}
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#C9A227]" />
                            New Members (Last 7 Days)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-48 flex items-end justify-between gap-2 pt-4">
                            {analytics.members.daily_trend.map((day, idx) => {
                                const maxMembers = Math.max(...analytics.members.daily_trend.map(d => d.count), 1);
                                const height = (day.count / maxMembers) * 100;
                                return (
                                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                                        <div className="w-full bg-[#3D4435] rounded-t relative" style={{ height: '140px' }}>
                                            <div 
                                                className="absolute bottom-0 w-full bg-green-500 rounded-t transition-all duration-500"
                                                style={{ height: `${height}%` }}
                                            />
                                        </div>
                                        <span className="text-[#A0A0A0] text-xs">
                                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                                        </span>
                                        <span className="text-white text-xs font-medium">{day.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Detailed Stats Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top Pages */}
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-[#C9A227]" />
                            Top Pages
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {analytics.page_views.top_pages.length > 0 ? (
                            <SimpleBarChart 
                                data={analytics.page_views.top_pages}
                                labelKey="page"
                                valueKey="views"
                            />
                        ) : (
                            <p className="text-[#A0A0A0] text-sm text-center py-4">No page view data yet</p>
                        )}
                    </CardContent>
                </Card>

                {/* Top Resources */}
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <ExternalLink className="w-5 h-5 text-[#C9A227]" />
                            Top Resources Accessed
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {analytics.resources.top_resources.length > 0 ? (
                            <SimpleBarChart 
                                data={analytics.resources.top_resources}
                                labelKey="name"
                                valueKey="count"
                            />
                        ) : (
                            <p className="text-[#A0A0A0] text-sm text-center py-4">No resource access data yet</p>
                        )}
                    </CardContent>
                </Card>

                {/* Top Engagers */}
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-[#C9A227]" />
                            Most Active Members
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {analytics.engagement.top_engagers.length > 0 ? (
                            <div className="space-y-3">
                                {analytics.engagement.top_engagers.map((engager, idx) => (
                                    <div key={idx} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-[#C9A227] flex items-center justify-center text-[#3D4435] font-bold text-sm">
                                                {idx + 1}
                                            </div>
                                            <div>
                                                <p className="text-white text-sm font-medium">{engager.name || 'Unknown'}</p>
                                                <p className="text-[#A0A0A0] text-xs">@{engager.username}</p>
                                            </div>
                                        </div>
                                        <span className="text-[#C9A227] font-medium">{engager.message_count} msgs</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-[#A0A0A0] text-sm text-center py-4">No messaging activity yet</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Engagement Summary */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-2">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#C9A227]" />
                        Engagement Summary
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                        <div className="text-center p-4 bg-[#3D4435] rounded-xl">
                            <p className="text-3xl font-bold text-white">{analytics.engagement.total_conversations}</p>
                            <p className="text-[#A0A0A0] text-sm mt-1">Total Conversations</p>
                        </div>
                        <div className="text-center p-4 bg-[#3D4435] rounded-xl">
                            <p className="text-3xl font-bold text-white">{analytics.engagement.active_conversations_this_week}</p>
                            <p className="text-[#A0A0A0] text-sm mt-1">Active This Week</p>
                        </div>
                        <div className="text-center p-4 bg-[#3D4435] rounded-xl">
                            <p className="text-3xl font-bold text-white">{analytics.resources.total_accesses}</p>
                            <p className="text-[#A0A0A0] text-sm mt-1">Resource Clicks</p>
                        </div>
                        <div className="text-center p-4 bg-[#3D4435] rounded-xl">
                            <p className="text-3xl font-bold text-white">{analytics.members.new_this_month}</p>
                            <p className="text-[#A0A0A0] text-sm mt-1">New Members (30d)</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminAnalyticsPage;
